import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { AttachmentModel, OfficeMimeTypes, PreviewableMimeTypes } from '@models/module/fields/main';
import { OfficeService } from '@module-instance/office/office.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class FileService {
  private officeService = inject(OfficeService);
  private dialog = inject(MatDialog);
  private userService = inject(UserService);

  downloadBase64File(base64Data: string, fileName: string, mimeType: string) {
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeType });
    this.downloadFile(blob, fileName);
  }

  downloadFile(blob: Blob, fileName: string) {
    const downloadLink = document.createElement('a');
    downloadLink.href = URL.createObjectURL(blob);
    downloadLink.download = fileName;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    URL.revokeObjectURL(downloadLink.href);
  }

  downloadMultipleBase64Files(files: string[], fileNames: string[], mimeType: string) {
    files.forEach((file, index) => {
      this.downloadBase64File(file, fileNames[index], mimeType);
    });
  }

  convertFileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Remove the data URL prefix (e.g., "data:application/pdf;base64,")
        const base64 = (reader.result as string).split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  }

  /**
   * Opens a file for preview or editing based on its MIME type.
   * Office files are opened in the Office Online viewer via the WOPI host;
   * other previewable files are opened in the attachment dialog.
   * @param attachments - the list of attachments to browse
   * @param index - index of the file to open within the list
   * @param readOnly - WI-8965: must be explicitly supplied by the caller so the WOPI host enforces
   * the correct permission. Pass false to allow editing; pass true to lock the file.
   */
  openFile(attachments: AttachmentModel[], index = 0, readOnly: boolean) {
    const file = attachments[index];
    if (OfficeMimeTypes.includes(file.mimeType)) {
      this.officeService.open(file, readOnly);
    } else if (PreviewableMimeTypes.includes(file.mimeType)) {
      const previewableAttachments = attachments.filter(att => PreviewableMimeTypes.includes(att.mimeType));
      this.openAttachmentDialog(previewableAttachments, file.id);
    }
  }

  async openAttachmentDialog(attachments: AttachmentModel[], startFileId: number) {
    const { NutrientComponent } = await import('@module-definition/nutrient/nutrient.component');
    this.dialog.open(NutrientComponent, {
      panelClass: ['cipo-dialog', 'classic', 'cipo--height--full'],
      ...this.userService.getResponsiveDialogSize().xl,
      data: {
        documents: attachments,
        startIndex: attachments.findIndex(a => a.id === startFileId),
      },
    });
  }
}
